import { useMutation } from '@tanstack/react-query';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';

import { endpointPortfolioCompanyPOST } from '~/api/def/endpoints/portfolioCompanyEndpoint';
import {
  CreatePortfolioCompany,
  CreatePortfolioCompanyValues,
  OnSubmit,
} from '~/components/forms/CreatePortfolioCompany';
import { uploadLogo } from '~/components/forms/utils/uploadLogo';

export default function CreatePortfolioCompanyContainer() {
  const toast = useRef<Toast>(null);

  const logoMutation = useMutation({
    mutationFn: async (data: { logoFile: File; id: string }) => {
      await uploadLogo(data.logoFile, data.id);
    },
    onSuccess: () => {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Company created successfully',
      });
    },
    onError: () => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to upload logo',
      });
    },
  });

  const companyMutation = useMutation({
    mutationFn: (data: CreatePortfolioCompanyValues) => {
      return endpointPortfolioCompanyPOST(
        data,
        (data.shareholders?.length ?? 0) > 0,
      );
    },
    onError: () => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create company',
      });
    },
  });

  const onSubmit: OnSubmit = async (data) => {
    const company = await companyMutation.mutateAsync(data);

    if (!data.logoFile) {
      return toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Company created successfully',
      });
    }

    await logoMutation.mutateAsync({
      logoFile: data.logoFile,
      id: company.data.id,
    });
  };

  return (
    <div
      className="flex flex-column mt-3 w-full p-3 gray-border"
      style={{
        borderRadius: '8px',
      }}
    >
      <Toast ref={toast} position="bottom-center" />
      <div>
        <h3 className="w-full text-center pb-4">Create Company</h3>
        <div>
          <CreatePortfolioCompany
            onSubmit={onSubmit}
            isLoading={companyMutation.isPending || logoMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
}
